import { persistentMap } from '@nanostores/persistent';
import { createId } from '@paralleldrive/cuid2';

import type { Price } from '@/types/products/single-issue';

const LOCAL_STORAGE_KEY = 'hb_shopping_cart';

export type CartValue = {
  cartId: string;
  items: Array<Pick<Price, 'id' | 'quantity'>>;
};

export const shoppingCart = persistentMap<Record<string, string>>(LOCAL_STORAGE_KEY, {
  cartId: (() => {
    const storedCart = localStorage.getItem(LOCAL_STORAGE_KEY);
    return storedCart ? JSON.parse(storedCart).cartId : createId();
  })(),
  items: (() => {
    const storedCart = localStorage.getItem(LOCAL_STORAGE_KEY);
    return storedCart ? JSON.parse(storedCart).items : JSON.stringify([]);
  })(),
});

export function setCartId(newCartId: string) {
  shoppingCart.setKey('cartId', newCartId);
}

export function addItemToCart(price: Price): Promise<boolean> {
  const item = { id: price.id, quantity: 1 };
  const itemsString = shoppingCart.get().items;
  const currentItems = itemsString ? JSON.parse(itemsString) : [];
  const itemIndex = currentItems.findIndex((cartItem) => cartItem.id === item.id);

  if (itemIndex !== -1) {
    currentItems[itemIndex].quantity += item.quantity;
  } else {
    currentItems.push(item);
  }

  shoppingCart.setKey('items', JSON.stringify(currentItems));
  return Promise.resolve(true);
}

export function removeItemFromCart(itemId: string) {
  const currentItems = JSON.parse(shoppingCart.get().items) as Array<{ id: string; quantity: number }>;
  const updatedItems = currentItems
    .map((item) => {
      if (item.id === itemId) {
        return { ...item, quantity: item.quantity - 1 };
      }
      return item;
    })
    .filter((item) => item.quantity > 0);
  shoppingCart.setKey('items', JSON.stringify(updatedItems));
}

export function clearCartIfEmpty() {
  const currentItems = JSON.parse(shoppingCart.get().items) as Array<{ id: string; quantity: number }>;
  const totalQuantity = currentItems.reduce((sum, item) => sum + item.quantity, 0);

  if (totalQuantity === 0) {
    shoppingCart.set({});
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  }
}

export function clearCartAfterCheckout() {
  shoppingCart.set({});
  localStorage.removeItem(LOCAL_STORAGE_KEY);
}

export function parseCartItems(items: string) {
  return JSON.parse(items).map((item: { id: string; quantity: number }) => ({
    price: item.id,
    quantity: item.quantity,
  }));
}

export function updateItemQuantity(itemId: string, newQuantity: number) {
  const currentItems = JSON.parse(shoppingCart.get().items) as Array<{ id: string; quantity: number }>;
  const updatedItems = currentItems
    .map((item) => {
      if (item.id === itemId) {
        return { ...item, quantity: newQuantity };
      }
      return item;
    })
    .filter((item) => item.quantity > 0);
  shoppingCart.setKey('items', JSON.stringify(updatedItems));
}

export function getTotalItemsCount() {
  const currentItems = parseCartItems(shoppingCart.get().items);
  return currentItems.reduce((sum, item) => sum + item.quantity, 0);
}

export function getTotalUniqueItems() {
  const currentItems = parseCartItems(shoppingCart.get().items);
  return currentItems.length;
}

export function updateCartItemQuantities(priceId: string, lineItemId: string, newQuantity: number) {
  updateItemQuantity(priceId, newQuantity);
  return lineItemId;
}

export function deleteItemFromCart(priceId: string) {
  const currentItems = JSON.parse(shoppingCart.get().items) as Array<{ id: string; quantity: number }>;
  const updatedItems = currentItems.filter((item) => item.id !== priceId);
  shoppingCart.setKey('items', JSON.stringify(updatedItems));

  if (updatedItems.length === 0) {
    clearCartIfEmpty();
  }
}
